// React and related imports
import React, { useState } from 'react';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';
import {
  clearValidationErrors,
  setValidationErrors,
} from '../../redux/slices/contentContextSlice';
import {
  selectFormHasErrors,
  selectValidationErrors,
} from '../../redux/selectors/contentContextSelectors';

// RTK Query hooks
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from '../../redux/api/flowApi';

// Interfaces
import { IAnswer } from '../../common/interfaces/Answer.interface';
import { IOption } from '../../common/interfaces/Option.interface';

// Flowbite components
import { Alert, Badge, Button, Modal } from 'flowbite-react';

// Icons
import { HiInformationCircle } from 'react-icons/hi';

// Custom components
import { MultiLangAnswerInput } from './MultiLangAnswerInput';
import { AlertContentVisibility } from '../Shared/AlertContentVisibility';

// Custom Hooks
import { usePreventUnload } from '../../hooks/usePreventUnload';
import { useFormChanges } from '../../hooks/useFormChanges';

// Utilis
import { checkFieldValidity } from '../../utils/checkFieldValidity';

interface AnswerModalProps {
  answer: IAnswer;
  show: boolean;
  isModalForAdd: boolean;
  onClose: () => void;
}

export const AnswerModal: React.FC<AnswerModalProps> = ({
  answer,
  show,
  isModalForAdd,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;
  const [selectedAnswer, setSelectedAnswer] = useState<IAnswer>(answer);
  const hasErrors = useAppSelector(selectFormHasErrors);
  const formErrors = Object.values(
    useAppSelector(selectValidationErrors)
  ).flat();

  const { hasChanges, hasChangesRef, resetInitialValues } =
    useFormChanges<IAnswer>({
      initialValues: answer,
      currentValues: selectedAnswer,
    });

  usePreventUnload({
    shouldPrevent: show && hasChanges,
  });

  const [createAnswer, { isLoading: creating }] = useCreateAnswerMutation();
  const [updateAnswer, { isLoading: updating }] = useUpdateAnswerMutation();

  const isLoadingState = updating || creating;

  const updateAnswerOptions = (value: IOption[]) => {
    setSelectedAnswer((prevState) => ({
      ...prevState,
      dataSources: [
        {
          options: value,
        },
      ],
    }));

    const errors = checkFieldValidity('answerOptions', value);
    if (errors.length > 0) {
      dispatch(setValidationErrors({ field: 'answerOptions', errors }));
    } else {
      dispatch(clearValidationErrors('answerOptions'));
    }
  };

  const onCloseModal = () => {
    if (hasChangesRef.current) {
      const shouldClose = window.confirm(
        t('content.unsavedChangesCloseConfirm')
      );
      if (!shouldClose) return; // If user cancels, don't close the modal
    }
    onClose();
  };

  const handleSubmission = async (answer: IAnswer) => {
    try {
      if (isModalForAdd) {
        await createAnswer(answer).unwrap();
      } else {
        await updateAnswer(answer).unwrap();
      }
      resetInitialValues(onCloseModal);
    } catch (error) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  return (
    <Modal dismissible show={show} onClose={onCloseModal}>
      <Modal.Header className="modal-header">
        <div className="flex items-center">
          <span className="me-3">
            {isModalForAdd ? t('answers.addAnswer') : t('answers.editAnswer')}
          </span>
          <Badge
            size="sm"
            className="cursor-default bg-gray-200 text-gray-800 group-hover:bg-gray-200 dark:bg-gray-200 dark:text-gray-800 dark:group-hover:bg-gray-200"
          >
            {t(`answerTypes.${answer.type}`)}
          </Badge>
        </div>
      </Modal.Header>

      {/* Passing Criteria Alert  */}
      {formErrors && formErrors.length > 0 && (
        <Alert
          className={`m-auto mt-3 w-11/12`}
          withBorderAccent
          additionalContent={
            <AlertContentVisibility t={t} errors={formErrors} />
          }
          color="warning"
          icon={HiInformationCircle}
        >
          <span className="me-1 ms-1 font-extrabold">{t('alert.info')}</span>
          {isModalForAdd
            ? t('alert.answerAddMessage')
            : t('alert.answerEditMessage')}
        </Alert>
      )}

      <Modal.Body className="space-y-5">
        <MultiLangAnswerInput
          t={t}
          answer={answer}
          updateAnswerOptions={updateAnswerOptions}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className={`${buttonClass} me-4`}
          onClick={onCloseModal}
        >
          {t('content.cancel')}
        </Button>
        <Button
          type="button"
          className={buttonClass}
          onClick={() => handleSubmission(selectedAnswer)}
          disabled={
            (!hasChanges && !isModalForAdd) || hasErrors || isLoadingState
          }
        >
          {t('content.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
